import {
    Button,
    DatePicker,
    Divider,
    Form,
    Input,
    notification,
    Select,
    Upload,
} from 'antd';
import { useTranslation } from 'react-i18next';
import ImgCrop from 'antd-img-crop';
import { useEffect, useState } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import Sticky from 'react-sticky-el';

import BtnSave from './BtnSave';
import GlobalService from '../../../../services/Global.service';
import { INIT, VALUE } from './init';
import { POLICY_NAME } from '../../POLICY';

export const checkValidForm = (fields, values) => {
    for (const [key, value] of Object.entries(values)) {
        if (fields.includes(key) && (!value || value?.length === 0)) {
            return false;
        }
    }
    return true;
};

const Step1 = (props) => {
    const { previewData, setStep1 } = props;
    const { t, i18n } = useTranslation(['translation', 'cookies-policy']);
    let lang = i18n.language;
    const values = props.form.getFieldsValue();
    const [fileList, setFileList] = useState([]);

    useEffect(() => {
        if (previewData) {
            let check = true;
            const allowKey = [
                'cookieCompanyNameTh',
                'cookieCompanyNameEn',
                'cookiePolicyNameEn',
                'cookiePolicyNameTh',
                'cookieEffectiveDate',
                'cookiePolicyDataSubjectType',
            ];
            for (const [key, value] of Object.entries(
                previewData?.cookiePolicy
            )) {
                if (allowKey.includes(key) && !value) {
                    check = false;
                }
                if (
                    key === 'cookiePolicyDataSubjectType' &&
                    value?.length <= 0
                ) {
                    check = false;
                    return;
                }
            }
            setStep1(check);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewData]);

    const beforeUpload = (file) => {
        const isJpgOrPng =
            file.type === 'image/jpeg' || file.type === 'image/png';

        if (!isJpgOrPng) {
            notification['error']({
                message: t('COOKIE.YouUploadFile'),
            });
        }

        const isLt2M = file.size / 1024 / 1024 < 2;

        if (!isLt2M) {
            notification['error']({
                message: t('COOKIE.ImageMustSmaller'),
            });
        }

        return isJpgOrPng && isLt2M;
    };

    const uploadButton = (
        <div>
            <svg
                width={17}
                height={17}
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M9.16536 5.16732H7.83203V7.83398H5.16536V9.16732H7.83203V11.834H9.16536V9.16732H11.832V7.83398H9.16536V5.16732ZM8.4987 1.83398C4.8187 1.83398 1.83203 4.82065 1.83203 8.50065C1.83203 12.1807 4.8187 15.1673 8.4987 15.1673C12.1787 15.1673 15.1654 12.1807 15.1654 8.50065C15.1654 4.82065 12.1787 1.83398 8.4987 1.83398ZM8.4987 13.834C5.5587 13.834 3.16536 11.4407 3.16536 8.50065C3.16536 5.56065 5.5587 3.16732 8.4987 3.16732C11.4387 3.16732 13.832 5.56065 13.832 8.50065C13.832 11.4407 11.4387 13.834 8.4987 13.834Z"
                    fill="#1890FF"
                />
            </svg>
            <div className="-mt-2 text-blue-500 opacity-90">
                {t('policy.ChooseFile')}
            </div>
        </div>
    );

    const onPreview = async (file) => {
        let src = file.url;

        if (!src) {
            src = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj);

                reader.onload = () => resolve(reader.result);
            });
        }

        const image = new Image();
        image.src = src;
        const imgWindow = window.open(src);
        imgWindow?.document.write(image.outerHTML);
    };

    const onNextStep = () => {
        props.nextStep();
        props?.form
            .validateFields()
            .then(() => {
                props.setStep1(true);
            })
            .catch(() => props.setStep1(false));
    };

    const getImage = async (cookieCompanyLogoId) => {
        try {
            const { data } = await GlobalService.getImage(cookieCompanyLogoId);
            if (data.resultData?.imageId) {
                setFileList([
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: `data:image/png;base64,${data.resultData?.imageId}`,
                    },
                ]);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (values?.cookieCompanyLogoId) getImage(values?.cookieCompanyLogoId);
    }, [values?.cookieCompanyLogoId]);

    const uploadProps = {
        multiple: false,
        listType: 'picture-card',
        beforeUpload,
        accept: '.png,.jpg,.jpeg',
        className: 'policy',
        fileList,
        onPreview,
        onChange(info) {
            const { status } = info.file;
            if (status === 'uploading') {
                info.file.status = 'done';
            }
            if (status === 'done' || status === 'removed') {
                setFileList(info.fileList);
            }
        },
        onSuccess(res, file) {
            props?.form?.setFieldsValue({
                cookieCompanyLogoId: res?.imageId,
            });
            getImage(res?.imageId);
        },
        onError(err) {
            console.log('onError', err);
        },
        onRemove() {
            props?.form?.setFieldsValue({
                cookieCompanyLogoId: null,
            });
        },
        customRequest: async ({ file, onSuccess, onError, onProgress }) => {
            let fileList = new FormData();
            fileList.append('image', file);
            fileList.append('fileName', file.name);
            fileList.append('action', 'image-create-application');

            try {
                const onUploadProgress = ({ total, loaded }) => {
                    onProgress(
                        {
                            percent: Math.round((loaded / total) * 100).toFixed(
                                2
                            ),
                        },
                        file
                    );
                };
                const { data } = await GlobalService.uploadImage(
                    fileList,
                    onUploadProgress
                );
                if (data?.resultData?.imageId) {
                    onSuccess(data?.resultData, file);
                } else {
                    onError('error');
                }
            } catch (error) {
                onError(error);
            }
        },
    };
    useEffect(() => {
        if (props?.previewData?.cookiePolicy) {
            onValuesChange(null, props.previewData?.cookiePolicy);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.previewData]);

    const onValuesChange = (change, allValues) => {
        const fields = [
            VALUE.COOKIE_POLICY_NAME_TH,
            'cookiePolicyNameEn',
            'cookieEffectiveDate',
            'cookieCompanyNameTh',
            'cookieCompanyNameEn',
            'domainId',
            'cookiePolicyDataSubjectType',
        ];
        const check = checkValidForm(fields, allValues);
        props.setStep1(check);
    };

    return (
        <div className="px-4 block_">
            <div className="text-purple-PHX font-semibold text-2xl">
                {t(
                    'cookies-policy.Define the policy name and organization information',
                    {
                        ns: POLICY_NAME.COOKIES_POLICY,
                    }
                )}
            </div>
            <div className="text-gray-500 text-tiny">{t('policy.desEtc')}</div>
            <Divider />
            <Form
                scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center',
                }}
                onValuesChange={onValuesChange}
                name="cookiePolicy"
                layout="vertical"
                form={props?.form}
                initialValues={INIT.cookiePolicy}
            >
                <Form.Item
                    label={
                        <span className="text-[21px] font-bold">
                            {t('cookies-policy.Name of Cookies Policy', {
                                ns: POLICY_NAME.COOKIES_POLICY,
                            })}
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                    name={VALUE.COOKIE_POLICY_NAME_TH}
                >
                    <Input
                        addonBefore="TH"
                        size="large"
                        placeholder={`${t(
                            'cookies-policy.Name of Cookies Policy',
                            {
                                ns: POLICY_NAME.COOKIES_POLICY,
                            }
                        )} (TH)`}
                    />
                </Form.Item>
                <Form.Item
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                    name="cookiePolicyNameEn"
                >
                    <Input
                        addonBefore="EN"
                        size="large"
                        placeholder={`${t(
                            'cookies-policy.Name of Cookies Policy',
                            {
                                ns: POLICY_NAME.COOKIES_POLICY,
                            }
                        )} (EN)`}
                    />
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="cookieEffectiveDate"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.effectiveDate')}
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <DatePicker
                        allowClear={false}
                        size="large"
                        style={{ width: '100%' }}
                        placeholder="DD/MM/YYYY"
                        format="DD/MM/YYYY"
                    />
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="cookieCompanyNameTh"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.companyNameEn')}
                        </span>
                    }
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <Input
                        placeholder="ชื่อองค์กร หรือ ชื่อบุคคลเจ้าของ (TH)"
                        addonBefore="TH"
                        size="large"
                    />
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="cookieCompanyNameEn"
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <Input
                        placeholder="ชื่อองค์กร หรือ ชื่อบุคคลเจ้าของ (EN)"
                        addonBefore="EN"
                        size="large"
                    />
                </Form.Item>
                <Form.Item
                    label={
                        <span className="text-[21px] font-bold">
                            {t('Domain')}
                        </span>
                    }
                    className="mt-4"
                    name="domainId"
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <Select
                        placeholder={t('Select Domain')}
                        style={{ width: '100%' }}
                        size="large"
                        allowClear
                        onChange={(e) => {
                            props?.setDomainId(e);
                        }}
                    >
                        {props.domainList?.map((domain) => (
                            <Select.Option
                                key={domain?.domainId}
                                value={domain?.domainId}
                            >
                                {domain?.domainName}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item
                    className="mt-4"
                    name="cookieCompanyLogoId"
                    label={
                        <span className="text-[21px] font-bold">
                            {t('policy.companyLogoId')}
                        </span>
                    }
                >
                    <ImgCrop
                        beforeCrop={beforeUpload}
                        showGrid
                        rotationSlider
                        aspectSlider
                        showReset
                    >
                        <Upload {...uploadProps}>
                            {fileList.length < 1 && uploadButton}
                        </Upload>
                    </ImgCrop>
                </Form.Item>
                <div className="-mt-6 text-gray-400 text-10">
                    {lang === 'en'
                        ? '*Max size of 2 MB. (Allowed file types: jpg., .png.)'
                        : '*ขนาดสูงสุด 2 MB. (ประเภทไฟล์ที่อนุญาต: jpg., .png.)'}
                </div>
                <Form.Item
                    label={
                        <span className="text-[21px] font-bold">
                            {t(
                                'What types of data subjects does this policy apply to'
                            )}
                        </span>
                    }
                    className="mt-4"
                    name="cookiePolicyDataSubjectType"
                    rules={[
                        {
                            required: true,
                            message: t('errorText.requiredField'),
                        },
                    ]}
                >
                    <Select
                        placeholder={t('Select data subject type')}
                        style={{ width: '100%' }}
                        size="large"
                        allowClear
                        mode="multiple"
                    >
                        {props.dsType?.map((dsType) => (
                            <Select.Option key={dsType?.id} value={dsType?.id}>
                                {dsType?.nameTh}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Sticky
                    mode="bottom"
                    boundaryElement=".block_"
                    stickyClassName="bg-white p-4 shadow h-16 rounded-lg z-50"
                >
                    <div className="flex items-center">
                        <Button
                            type="primary"
                            size="large"
                            onClick={onNextStep}
                        >
                            {t('policy.NextStep')} <ArrowRightOutlined />
                        </Button>
                        <BtnSave saveDraft={props.saveDraft} />
                    </div>
                </Sticky>
            </Form>
        </div>
    );
};
export default Step1;
